// Centralized "force logout" + recovery state. Anropas från:
//   - axios response-interceptor (401/403 = ANSKNI-22, 5xx-burst = ANSKNI-56 #2)
//   - router.onError (ChunkLoadError = ANSKNI-56 #1)
//   - app.config.errorHandler / window.onerror (uncaught = ANSKNI-56 #4)
//
// Använder window.location.href för redirect — full page reload garanterar
// att all in-memory-state nollställs och fungerar oavsett vilken router
// (admin/job/recruit) som råkar vara aktiv eller om routern är i broken state.

const FAILURE_WINDOW_MS = 60_000;
const FAILURE_THRESHOLD = 5;

// Eventuellt 5xx-krav: ren network-glitch (offline VPN etc.) ska inte logga ut
// någon — bara om backend faktiskt svarar med fel räknar vi det som "stuck".
let recentFailures: { ts: number; isServerError: boolean }[] = [];

// Idempotens-guard: om flera anrop failar samtidigt vill vi inte trigga
// flera redirects parallellt eller hoppa loop:t mellan logout och login.
let logoutInFlight = false;

export type LogoutReason =
  | "session-expired"   // 401/403 från backend
  | "connection-lost"   // 5xx-burst → backend nere eller deploy mid-session
  | "app-error"         // uncaught JS exception
  | "version-mismatch"; // (reserverat för framtida version-poll)

export function forceLogout(reason: LogoutReason, returnPath?: string): void {
  if (logoutInFlight) return;
  logoutInFlight = true;

  // Rensa allt vi kan komma åt synkront. Det räcker för att "logga ut" — vid
  // nästa sidladdning är ingen token kvar i localStorage så app:en bootar utloggad.
  try {
    localStorage.removeItem("user_token");
    localStorage.removeItem("vuex");
  } catch { /* localStorage kan vara blockerad i private mode etc. */ }

  // Bygg redirect: behåll path så användaren kommer tillbaka efter ny login.
  const path = returnPath ?? window.location.pathname + window.location.search;
  const params = new URLSearchParams({ reason });
  if (path && path !== "/" && !path.startsWith("/login")) {
    params.set("redirect", path);
  }

  // Hard navigate. Vue Router skulle kunna fungera men full reload är robust.
  window.location.href = `/login?${params.toString()}`;
}

export function recordRequestFailure(error: any): void {
  const status: number | undefined = error?.response?.status;
  const isServerError = status !== undefined && status >= 500;
  // Skip 4xx (utom 401/403 som hanteras separat) — de är client-fel som vi
  // inte ska konvertera till logout. Pure network errors (status undefined)
  // räknas men vi kräver minst en 5xx för att trigga.
  recentFailures.push({ ts: Date.now(), isServerError });
  pruneOldFailures();

  if (recentFailures.length < FAILURE_THRESHOLD) return;
  if (!recentFailures.some(f => f.isServerError)) return;

  // Threshold nått OCH minst en 5xx — backend är ur funktion eller har
  // deployat något som inte är kompatibelt med vår klient. Logga ut.
  forceLogout("connection-lost");
}

export function recordRequestSuccess(): void {
  // Återställ counter på lyckat svar — vi vill bara fånga BURSTS av fel.
  if (recentFailures.length > 0) recentFailures = [];
}

function pruneOldFailures(): void {
  const cutoff = Date.now() - FAILURE_WINDOW_MS;
  recentFailures = recentFailures.filter(f => f.ts > cutoff);
}

// Skip-lista: URLs som inte ska räknas in i fail-detektor och inte trigga
// auto-logout. Login + public-flöden får aldrig leda till re-direct loop.
const SKIP_URL_FRAGMENTS = ["/api/account/login", "/api/account/register", "/api/public/"];

export function shouldSkipForUrl(url: string | undefined): boolean {
  if (!url) return false;
  return SKIP_URL_FRAGMENTS.some(fragment => url.includes(fragment));
}

// ANSKNI-56 #1: ChunkLoadError-handler. När en deploy bytt namn på lazy-chunks
// kommer browsern (med gammalt index.html-cache) försöka ladda en chunk som inte
// längre finns → router throw:ar ChunkLoadError → Vue renderar inget. Lösning:
// reload sidan så browsern hämtar nya bundle. Sessionstorage-guarden hindrar
// infinite reload om problemet är något annat än cachat index.
const CHUNK_RELOAD_GUARD_KEY = "session-recovery:chunk-reload-attempted";

function isChunkLoadError(error: any): boolean {
  if (!error) return false;
  const name = error?.name;
  const message = String(error?.message ?? error ?? "");
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [^ ]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

function recoverFromChunkLoadError(): void {
  if (sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY)) {
    // Vi har redan reload:at en gång — det här är inte en deploy-cache-issue,
    // någonting annat är trasigt. Trigga app-error-vägen istället.
    forceLogout("app-error");
    return;
  }
  try { sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, "1"); } catch { /* ignore */ }
  window.location.reload();
}

export function attachRouterErrorHandler(router: {
  onError: (handler: (error: any) => void) => void;
  afterEach: (handler: () => void) => void;
}): void {
  router.onError((error) => {
    if (isChunkLoadError(error)) recoverFromChunkLoadError();
  });
  router.afterEach(() => {
    // Lyckad navigation = guarden behövs inte mer.
    try { sessionStorage.removeItem(CHUNK_RELOAD_GUARD_KEY); } catch { /* ignore */ }
  });
}

// ANSKNI-56 #4: globala error-handlers. Vue-komponent-fel går via
// app.config.errorHandler. Promise-rejections (inkl. async dynamic imports
// som inte fångas av router.onError) går via window-eventet. Inga av dessa
// trigger:ar logout direkt — vi LOG:ar för synlighet och recover:ar bara
// vid chunk-load-error som är säkert hanterbart med reload.
import type { App } from "vue";

export function attachGlobalErrorHandlers(app: App): void {
  app.config.errorHandler = (err: unknown, _instance: unknown, info: string) => {
    // eslint-disable-next-line no-console
    console.error("[session-recovery] Vue uncaught error", info, err);
    if (isChunkLoadError(err)) recoverFromChunkLoadError();
  };

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    if (isChunkLoadError(reason)) {
      // Förhindra att chunk-load-fel propageras vidare som ouppfångat —
      // vi tar hand om det med reload.
      event.preventDefault();
      recoverFromChunkLoadError();
    }
  });

  window.addEventListener("error", (event) => {
    if (isChunkLoadError(event?.error) || isChunkLoadError(event?.message)) {
      event.preventDefault();
      recoverFromChunkLoadError();
    }
  });
}
